import React, { useCallback, useMemo } from 'react'

import ListItem from 'components/Luxkit/List/ListItem'
import LandmarkIcon from 'components/Luxkit/Icons/custom/LandmarkIcon'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import LineCompassIcon from 'components/Luxkit/Icons/line/LineCompassIcon'
import Pill from 'components/Luxkit/Pill'
import Label from 'components/Luxkit/Label'
import switchFunc from 'lib/switchFunc'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'

export const SEARCH_MENU_LIST_ITEM_CLASS_NAME = 'search-menu-list-item'

interface Props {
  /** highlights the portion of word that matches the search phrase */
  searchPhrase?: string;
  searchItem: App.SearchItem
  onClick?: (searchItem: App.SearchItem, mouseEvent?: React.MouseEvent<HTMLElement>) => void
  ['data-testid']?: string
  selected?: boolean
}

const searchItemPropertiesSwitch = switchFunc({
  city: { Icon: LineMapMarkerIcon, typeIndicator: 'City' },
  state: { Icon: LineMapMarkerIcon, typeIndicator: 'State' },
  province: { Icon: LineMapMarkerIcon, typeIndicator: 'Province' },
  landmark: { Icon: LandmarkIcon, typeIndicator: 'Landmark' },
  property: { Icon: LineBedIcon, typeIndicator: 'Hotel' },
  experience: { Icon: LineCompassIcon, typeIndicator: null },
  destination: { Icon: LineMapMarkerIcon, typeIndicator: null },
  airport: { Icon: LinePlaneFlyIcon, typeIndicator: null },
}, { Icon: LineMapMarkerIcon, typeIndicator: null })

function SearchMenuListItem(props: Props) {
  const {
    searchItem,
    onClick,
    searchPhrase,
    selected = false,
  } = props

  const {
    Icon,
    subtitle,
    title,
    offerType,
    offerDiscount,
    isBedbankPromoOffer,
  } = useMemo(() => {
    const properties = searchItemPropertiesSwitch(searchItem.searchType === 'destination' ? searchItem.destinationType : searchItem.searchType)

    const { mainText, secondaryText } = searchItem.format
    const { Icon, typeIndicator } = properties

    const subtitleItems: string[] = []

    if (typeIndicator) {
      subtitleItems.push(typeIndicator)
    }

    if (
      secondaryText &&
      mainText.localeCompare(secondaryText, 'en', { sensitivity: 'base' }) !== 0
    ) {
      subtitleItems.push(secondaryText)
    }

    return {
      Icon,
      subtitle: subtitleItems.join(' · '),
      title: mainText,
      offerType: searchItem.searchType === 'property' ? searchItem.offerType : null,
      offerDiscount: searchItem.searchType === 'property' ? searchItem.offerDiscount : null,
      isBedbankPromoOffer: searchItem.searchType === 'property' ? searchItem.bedbankHasPromotions : false,
    }
  }, [searchItem])

  const handleClick = useCallback((mouseEvent: React.MouseEvent<HTMLElement>) => {
    onClick?.(searchItem, mouseEvent)
  }, [searchItem, onClick])

  const label = useMemo(() => {
    if (!searchPhrase) {
      return title
    }

    const search = new RegExp(`(${searchPhrase})`, 'ig')
    const bits = title.split(search)

    return <>
      {bits.map((textBit, index) => textBit.toLowerCase() === searchPhrase.toLowerCase() ? <b key={index}>{textBit}</b> : textBit, '')}
    </>
  }, [searchPhrase, title])

  return <ListItem
    selected={selected}
    className={SEARCH_MENU_LIST_ITEM_CLASS_NAME}
    startIcon={<Icon />}
    title={label}
    subtitle={<>
      {subtitle}
      { (offerType === 'hotel' || isBedbankPromoOffer) && (
        <LabelGroup>
          { offerType === 'hotel' && <Label variant="highlight-secondary" kind="plain">Limited Time Offer</Label>}
          { isBedbankPromoOffer && <Label variant="highlight-secondary" kind="plain">Limited Time Special</Label>}
          { !!offerDiscount && offerDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && (
            <Pill kind="primary" variant="success">Save up to { offerDiscount }%</Pill>
          )}
        </LabelGroup>
      ) }
    </>}
    onClick={handleClick}
    data-testid={props['data-testid']}
  />
}

export default SearchMenuListItem
